import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel, 
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Fade,
  Chip
} from '@mui/material';
import styled from 'styled-components';
import './DiamondMatchmaker.css';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import WhatsAppButton from '../../common/WhatsAppButton';
import useWhatsAppContact from '../../../hooks/useWhatsAppContact';

// Styled components
const PageWrapper = styled.div`
  background-color: #121218;
  min-height: 100vh;
  width: 100%;
  color: white;
  position: relative;
  z-index: 1;
`;

const MatchmakerContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 20px 40px;
  min-height: calc(100vh - 120px); /* Account for padding */
  background-color: #121218;
  width: 100%;
  position: relative;
  z-index: 1;
`;

const PageTitle = styled.h1`
  color: white !important;
  margin-bottom: 8px !important;
  text-align: center;
  font-weight: bold !important;
  font-size: 2.5rem !important;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: -5px;
    left: 50%;
    background: linear-gradient(90deg, #3498db, #8e44ad);
    transition: all 0.3s ease;
    transform: translateX(-50%);
    opacity: 0;
  }
  
  &:hover::after {
    width: 100%;
    opacity: 1;
  }
`;

const PageSubtitle = styled.h2`
  color: #aaa !important;
  margin-bottom: 32px !important;
  text-align: center;
  font-size: 1.1rem !important;
  font-weight: normal;
`;

const QuestionCard = styled(Card)`
  background: rgba(30, 30, 40, 0.8);
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  margin: 20px 0;
  overflow: visible;
  position: relative;
  backdrop-filter: blur(10px);
  
  &:before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #3498db, #8e44ad, #3498db);
    z-index: -1;
    border-radius: 16px;
    opacity: 0.7;
    animation: borderGlow 3s infinite alternate;
  }
  
  @keyframes borderGlow {
    0% {
      opacity: 0.5;
      box-shadow: 0 0 20px rgba(52, 152, 219, 0.3);
    }
    100% {
      opacity: 0.8;
      box-shadow: 0 0 30px rgba(142, 68, 173, 0.5);
    }
  }
`;

const OptionButton = styled(Button)`
  margin: 10px;
  padding: 15px 25px;
  border-radius: 10px;
  transition: all 0.3s ease;
  background: rgba(20, 20, 30, 0.8) !important;
  color: white !important;
  border: 1px solid rgba(100, 100, 255, 0.3) !important;
  text-transform: uppercase;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 7px 14px rgba(50, 50, 255, 0.2);
    background: rgba(30, 30, 50, 0.9) !important;
    border-color: rgba(100, 100, 255, 0.8) !important;
  }
  
  &.selected {
    background: linear-gradient(45deg, #3498db, #8e44ad) !important;
    color: white !important;
    border: none !important;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
`;

const ResultCard = styled(Card)`
  background: rgba(25, 25, 35, 0.9);
  border-radius: 15px;
  overflow: hidden;
  margin: 15px 0;
  transition: all 0.3s ease;
  border: 1px solid rgba(100, 100, 255, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(50, 50, 255, 0.3);
    border-color: rgba(100, 100, 255, 0.6);
  }
`;

const MatchScore = styled.div`
  position: absolute;
  top: -15px;
  right: 20px;
  background: linear-gradient(45deg, #3498db, #8e44ad);
  color: white;
  border-radius: 20px;
  padding: 5px 15px;
  font-weight: bold;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
`;

const DiamondImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(25,25,35,1) 100%);
  }
`;

// Sample questions for the personality assessment
const questions = [
  {
    id: 1,
    text: "What's your personal style?",
    options: [
      { id: 'a', text: "Classic and timeless", value: { shape: "round", color: "D-F", clarity: "VS" } },
      { id: 'b', text: "Modern and unique", value: { shape: "princess", color: "G-H", clarity: "SI" } },
      { id: 'c', text: "Vintage-inspired", value: { shape: "cushion", color: "I-J", clarity: "VS" } },
      { id: 'd', text: "Bold and dramatic", value: { shape: "emerald", color: "D-F", clarity: "VVS" } }
    ]
  },
  {
    id: 2,
    text: "How would you describe your lifestyle?",
    options: [
      { id: 'a', text: "Active and outdoorsy", value: { setting: "bezel", size: "medium" } },
      { id: 'b', text: "Professional and polished", value: { setting: "prong", size: "large" } },
      { id: 'c', text: "Creative and artistic", value: { setting: "halo", size: "medium" } },
      { id: 'd', text: "Luxurious and sophisticated", value: { setting: "pave", size: "large" } }
    ]
  },
  {
    id: 3,
    text: "What qualities matter most to you in a diamond?",
    options: [
      { id: 'a', text: "Brilliance and sparkle", value: { cut: "excellent", clarity: "VS" } },
      { id: 'b', text: "Size and presence", value: { cut: "very good", size: "large" } },
      { id: 'c', text: "Color and purity", value: { color: "D-F", clarity: "VVS" } },
      { id: 'd', text: "Uniqueness and character", value: { shape: "unique", color: "fancy" } }
    ]
  },
  {
    id: 4,
    text: "What's the primary occasion for this diamond?",
    options: [
      { id: 'a', text: "Engagement", value: { purpose: "engagement", setting: "solitaire" } },
      { id: 'b', text: "Anniversary gift", value: { purpose: "gift", setting: "three-stone" } },
      { id: 'c', text: "Personal milestone", value: { purpose: "personal", setting: "fashion" } },
      { id: 'd', text: "Investment", value: { purpose: "investment", quality: "premium" } }
    ]
  },
  {
    id: 5,
    text: "What's your budget range?",
    options: [
      { id: 'a', text: "₹50,000 - ₹1,00,000", value: { budget: "entry", size: "small" } },
      { id: 'b', text: "₹1,00,000 - ₹3,00,000", value: { budget: "mid", size: "medium" } },
      { id: 'c', text: "₹3,00,000 - ₹7,00,000", value: { budget: "high", size: "large" } },
      { id: 'd', text: "₹7,00,000+", value: { budget: "premium", size: "xlarge" } }
    ]
  }
];

// Sample diamond data
const sampleDiamonds = [
  {
    id: 1,
    name: "Brilliant Round Solitaire",
    shape: "round",
    carat: 1.2,
    color: "F",
    clarity: "VS1",
    cut: "excellent",
    setting: "prong",
    price: 350000,
    image: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A stunning round brilliant diamond with exceptional sparkle and fire. Perfect for those who value timeless elegance and maximum brilliance."
  },
  {
    id: 2,
    name: "Princess Cut Halo",
    shape: "princess",
    carat: 1.5,
    color: "G",
    clarity: "SI1",
    cut: "very good",
    setting: "halo",
    price: 420000,
    image: "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A modern princess cut diamond surrounded by a halo of smaller diamonds. Offers excellent presence and sparkle for those with contemporary taste."
  },
  {
    id: 3,
    name: "Vintage Cushion Cut",
    shape: "cushion",
    carat: 1.3,
    color: "I",
    clarity: "VS2",
    cut: "very good",
    setting: "vintage",
    price: 280000,
    image: "https://images.unsplash.com/photo-1600267185393-e158a98703de?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A romantic cushion cut diamond with vintage-inspired details. Ideal for those who appreciate classic designs with character and warmth."
  },
  {
    id: 4,
    name: "Emerald Cut Statement",
    shape: "emerald",
    carat: 2.0,
    color: "D",
    clarity: "VVS2",
    cut: "excellent",
    setting: "pave",
    price: 850000,
    image: "https://images.unsplash.com/photo-1608042314453-ae338d80c427?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A sophisticated emerald cut diamond with step facets that showcase exceptional clarity. Perfect for those who prefer understated luxury."
  },
  {
    id: 5,
    name: "Active Lifestyle Bezel",
    shape: "round",
    carat: 0.9,
    color: "H",
    clarity: "SI1",
    cut: "excellent",
    setting: "bezel",
    price: 180000,
    image: "https://images.unsplash.com/photo-1589674781759-c21c37956a44?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A secure bezel-set diamond that's perfect for active lifestyles. Offers protection while maintaining excellent brilliance."
  },
  {
    id: 6,
    name: "Investment Grade Oval",
    shape: "oval",
    carat: 2.5,
    color: "E",
    clarity: "VVS1",
    cut: "excellent",
    setting: "solitaire",
    price: 1200000,
    image: "https://images.unsplash.com/photo-1615655114865-4cc01c667b6d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
    description: "A premium oval diamond with exceptional quality in all aspects. Ideal as both a stunning piece of jewelry and a valuable investment."
  }
];

const DiamondMatchmaker = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(questions[0]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [matchResults, setMatchResults] = useState([]);
  const [personalityProfile, setPersonalityProfile] = useState(null);
  const { createDiamondProduct } = useWhatsAppContact();
  
  const steps = ['Style', 'Lifestyle', 'Preferences', 'Occasion', 'Budget', 'Results'];
  
  // Ensure background color persists only within the component
  useEffect(() => {
    // No need to modify the body background color anymore
    // The component will handle its own background
    
    return () => {
      // No cleanup needed
    };
  }, []);
  
  useEffect(() => {
    setCurrentQuestion(questions[activeStep]);
    setSelectedOption(null);
  }, [activeStep]);
  
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };
  
  const handleNext = () => {
    if (selectedOption) {
      // Save the answer
      setAnswers({
        ...answers,
        [currentQuestion.id]: selectedOption
      });
      
      if (activeStep === questions.length - 1) {
        // Last question answered, analyze results
        analyzeResults();
      } else {
        // Move to next question
        setActiveStep(activeStep + 1);
      }
    }
  };
  
  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  
  const analyzeResults = () => {
    setIsAnalyzing(true);
    
    // Simulate AI processing time
    setTimeout(() => {
      // Create a personality profile based on answers
      const profile = createPersonalityProfile();
      setPersonalityProfile(profile);
      
      // Match with diamonds
      const results = matchDiamonds(profile);
      setMatchResults(results);
      
      setIsAnalyzing(false);
      setActiveStep(activeStep + 1);
    }, 3000);
  };
  
  const createPersonalityProfile = () => {
    // This would be a sophisticated algorithm in a real implementation
    // Here we'll just combine the values from all answers
    let profile = {
      style: {},
      preferences: {},
      priorities: []
    };
    
    Object.values(answers).forEach(answer => {
      Object.entries(answer.value).forEach(([key, value]) => {
        if (typeof value === 'string') {
          profile.style[key] = value;
        } else {
          profile.preferences[key] = value;
        }
      });
    });
    
    // Determine top priorities
    if (profile.style.shape) profile.priorities.push('shape');
    if (profile.style.color) profile.priorities.push('color');
    if (profile.style.clarity) profile.priorities.push('clarity');
    if (profile.preferences.cut) profile.priorities.push('cut');
    
    return profile;
  };
  
  const matchDiamonds = (profile) => {
    // In a real implementation, this would use a sophisticated algorithm
    // Here we'll do a simple matching based on the profile
    
    return sampleDiamonds.map(diamond => {
      let matchScore = 0;
      let matchReasons = [];
      
      // Check shape match
      if (profile.style.shape === diamond.shape) {
        matchScore += 25;
        matchReasons.push(`${diamond.shape} shape matches your style preference`);
      }
      
      // Check color match
      if (profile.style.color && profile.style.color.includes(diamond.color)) {
        matchScore += 20;
        matchReasons.push(`${diamond.color} color aligns with your preference`);
      }
      
      // Check clarity match
      if (profile.style.clarity && diamond.clarity.startsWith(profile.style.clarity)) {
        matchScore += 15;
        matchReasons.push(`${diamond.clarity} clarity is ideal for your priorities`);
      }
      
      // Check cut match
      if (profile.preferences.cut === diamond.cut) {
        matchScore += 15;
        matchReasons.push(`${diamond.cut} cut provides the brilliance you value`);
      }
      
      // Check setting match
      if (profile.style.setting === diamond.setting) {
        matchScore += 15;
        matchReasons.push(`${diamond.setting} setting suits your lifestyle`);
      }
      
      // Budget considerations
      const budget = answers[5]?.value.budget;
      if (budget === "entry" && diamond.price < 100000) {
        matchScore += 10;
        matchReasons.push("fits within your budget range");
      } else if (budget === "mid" && diamond.price >= 100000 && diamond.price < 300000) {
        matchScore += 10;
        matchReasons.push("fits perfectly within your budget range");
      } else if (budget === "high" && diamond.price >= 300000 && diamond.price < 700000) {
        matchScore += 10;
        matchReasons.push("fits well within your budget range");
      } else if (budget === "premium" && diamond.price >= 700000) {
        matchScore += 10;
        matchReasons.push("matches your premium budget expectations");
      }
      
      return {
        ...diamond,
        matchScore,
        matchReasons
      };
    }).sort((a, b) => b.matchScore - a.matchScore);
  };
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };
  
  const renderQuestion = () => {
    if (!currentQuestion) return null;
    
    return (
      <Fade in={true} timeout={800}>
        <QuestionCard>
          <CardContent>
            <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
              {currentQuestion.text}
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={2}>
                {currentQuestion.options.map((option) => (
                  <Grid item xs={12} sm={6} key={option.id}>
                    <Button
                      fullWidth
                      variant="outlined"
                      className={`option-button ${selectedOption?.id === option.id ? 'selected' : ''}`}
                      onClick={() => handleOptionSelect(option)}
                      sx={{
                        backgroundColor: selectedOption?.id === option.id ? 'transparent' : 'rgba(30, 30, 40, 0.8)',
                        color: 'white',
                        padding: '16px 24px',
                        borderRadius: '8px',
                        border: selectedOption?.id === option.id ? 'none' : '1px solid rgba(100, 100, 255, 0.3)',
                        fontSize: '1.1rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: selectedOption?.id === option.id 
                          ? '0 0 15px rgba(74, 144, 226, 0.5)' 
                          : '0 2px 5px rgba(0, 0, 0, 0.2)',
                        transform: selectedOption?.id === option.id ? 'scale(1.02)' : 'scale(1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: selectedOption?.id === option.id ? 'transparent' : 'rgba(40, 40, 60, 0.9)',
                          borderColor: 'rgba(100, 100, 255, 0.6)',
                          transform: 'scale(1.01)',
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)'
                        }
                      }}
                    >
                      {option.text}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.3)',
                  padding: '10px 24px',
                  fontSize: '1rem',
                  fontWeight: 'medium',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!selectedOption}
                sx={{ 
                  background: 'linear-gradient(45deg, #3498db, #8e44ad)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 28px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #2980b9, #8e44ad)',
                    boxShadow: '0 6px 15px rgba(0, 0, 0, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&.Mui-disabled': {
                    background: 'rgba(80, 80, 100, 0.3)',
                    color: 'rgba(255,255,255,0.4)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {activeStep === questions.length - 1 ? 'Find My Perfect Diamond' : 'Next'}
              </Button>
            </Box>
          </CardContent>
        </QuestionCard>
      </Fade>
    );
  };
  
  const renderAnalyzing = () => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <CircularProgress size={80} sx={{ color: '#3498db', mb: 4 }} />
        <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
          Analyzing Your Preferences
        </Typography>
        <Typography variant="body1" sx={{ color: '#aaa', textAlign: 'center', maxWidth: '600px' }}>
          Our AI is finding your perfect diamond match based on your unique personality and preferences...
        </Typography>
      </Box>
    );
  };
  
  const renderResults = () => {
    return (
      <Fade in={true} timeout={1000}>
        <Box>
          <Typography variant="h3" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
            Your Diamond Matches
          </Typography>
          
          {personalityProfile && (
            <Box sx={{ 
              mb: 4, 
              mt: 2, 
              p: 3, 
              background: 'rgba(20,20,30,0.7)', 
              borderRadius: '10px',
              border: '1px solid rgba(52, 152, 219, 0.3)',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                background: 'linear-gradient(90deg, #3498db, #8e44ad, #2ecc71)',
              }
            }}>
              <Typography variant="h5" sx={{ 
                color: '#3498db', 
                mb: 2,
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>
                Your Diamond Personality Profile
              </Typography>
              <Typography variant="body1" sx={{ 
                color: '#ddd', 
                mb: 2,
                lineHeight: 1.6
              }}>
                Based on your responses, you value {personalityProfile.priorities.slice(0, 2).join(' and ')} in your diamonds.
                Your style preferences lean towards {personalityProfile.style.shape || 'versatile'} shapes with 
                {personalityProfile.style.color ? ` ${personalityProfile.style.color} color range` : ' balanced color'}.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                {Object.entries(personalityProfile.style).map(([key, value]) => (
                  <Chip 
                    key={key} 
                    label={`${key}: ${value}`} 
                    sx={{ 
                      background: 'rgba(52, 152, 219, 0.2)', 
                      color: 'white',
                      border: '1px solid rgba(52, 152, 219, 0.5)',
                      '&:hover': {
                        background: 'rgba(52, 152, 219, 0.3)',
                        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                      },
                      transition: 'all 0.2s ease'
                    }} 
                  />
                ))}
              </Box>
            </Box>
          )}
          
          <Grid container spacing={3}>
            {matchResults.slice(0, 3).map((diamond, index) => (
              <Grid item xs={12} md={4} key={diamond.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <ResultCard>
                    <MatchScore>{diamond.matchScore}% Match</MatchScore>
                    <DiamondImage style={{ backgroundImage: `url(${diamond.image})` }} />
                    <CardContent>
                      <Typography variant="h5" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                        {diamond.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#aaa', mb: 2 }}>
                        {diamond.carat} carat {diamond.shape} • {diamond.color} color • {diamond.clarity} clarity
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#ddd', mb: 3 }}>
                        {diamond.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                        {diamond.matchReasons.slice(0, 3).map((reason, i) => (
                          <Chip 
                            key={i} 
                            label={reason} 
                            size="small"
                            sx={{ 
                              background: 'rgba(142, 68, 173, 0.2)', 
                              color: '#ddd',
                              border: '1px solid rgba(142, 68, 173, 0.5)'
                            }} 
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ color: '#3498db', fontWeight: 'bold' }}>
                          {formatPrice(diamond.price)}
                        </Typography>
                        <WhatsAppButton 
                          product={createDiamondProduct({
                            name: diamond.name,
                            carat: diamond.carat,
                            clarity: diamond.clarity,
                            color: diamond.color,
                            cut: diamond.cut,
                            shape: diamond.shape,
                            price: diamond.price,
                            image: diamond.image,
                            description: diamond.description
                          })}
                          buttonText="Contact Us"
                          showShareOptions={true}
                          buttonProps={{
                            sx: { 
                              background: 'linear-gradient(45deg, #3498db, #8e44ad)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontWeight: 'bold',
                              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
                              '&:hover': {
                                background: 'linear-gradient(45deg, #2980b9, #8e44ad)',
                                boxShadow: '0 6px 15px rgba(0, 0, 0, 0.4)',
                                transform: 'translateY(-2px)'
                              },
                              transition: 'all 0.2s ease'
                            }
                          }}
                        />
                      </Box>
                    </CardContent>
                  </ResultCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
          
          {matchResults.length > 3 && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button 
                variant="outlined"
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(52, 152, 219, 0.5)',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: 'medium',
                  fontSize: '0.95rem',
                  '&:hover': {
                    borderColor: '#3498db',
                    backgroundColor: 'rgba(52, 152, 219, 0.1)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(52, 152, 219, 0.3)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                View More Matches
              </Button>
            </Box>
          )}
          
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button 
              variant="contained"
              onClick={() => {
                setActiveStep(0);
                setAnswers({});
                setMatchResults([]);
                setPersonalityProfile(null);
              }}
              sx={{ 
                background: 'linear-gradient(45deg, #3498db, #8e44ad)',
                color: 'white',
                border: 'none',
                padding: '12px 28px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: '0 4px 15px rgba(142, 68, 173, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2980b9, #8e44ad)',
                  boxShadow: '0 6px 20px rgba(142, 68, 173, 0.6)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Start Over
            </Button>
          </Box>
        </Box>
      </Fade>
    );
  };

  return (
    <PageWrapper 
      className="diamond-matchmaker"
      style={{ 
        backgroundColor: '#121218', 
        position: 'relative',
        width: '100%', 
        minHeight: '100vh',
        overflowY: 'auto',
        paddingTop: '60px', // Add space for navbar
        paddingBottom: '60px' // Add space for footer
      }}>
      <MatchmakerContainer>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <PageTitle 
            className="title-underline-animation"
            style={{ 
              color: 'white', 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              textAlign: 'center',
              marginBottom: '8px',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
            }}>
            AI Diamond Matchmaker
          </PageTitle>
          <PageSubtitle style={{ 
            color: '#aaa', 
            fontSize: '1.1rem', 
            textAlign: 'center',
            marginBottom: '32px',
            fontWeight: 'normal'
          }}>
            Discover your perfect diamond based on your unique personality and preferences
          </PageSubtitle>
        </Box>
        
        {activeStep === questions.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
            <Button 
              component={Link} 
              to="/diamonds" 
              variant="contained"
              sx={{ 
                background: 'linear-gradient(45deg, #3498db, #2ecc71)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1rem',
                boxShadow: '0 4px 15px rgba(46, 204, 113, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2980b9, #27ae60)',
                  boxShadow: '0 6px 20px rgba(46, 204, 113, 0.6)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease',
                mr: 2
              }}
            >
              Get Price Estimate
            </Button>
          </Box>
        )}
        
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ 
            mb: 5, 
            '& .MuiStepConnector-line': {
              borderColor: 'rgba(52, 152, 219, 0.3)'
            },
            '& .MuiStepLabel-label': {
              marginTop: '8px'
            },
            '& .MuiStepIcon-root': {
              width: '35px',
              height: '35px'
            },
            '& .MuiStepIcon-text': {
              fill: '#121218',
              fontWeight: 'bold'
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                StepIconProps={{ 
                  sx: { 
                    color: activeStep >= steps.indexOf(label) ? '#3498db' : 'rgba(255, 255, 255, 0.2)',
                    '&.Mui-active': {
                      color: '#8e44ad',
                      filter: 'drop-shadow(0 0 8px rgba(142, 68, 173, 0.6))'
                    },
                    '&.Mui-completed': {
                      color: '#3498db',
                      filter: 'drop-shadow(0 0 5px rgba(52, 152, 219, 0.5))'
                    }
                  } 
                }}
              >
                <Typography sx={{ 
                  color: activeStep >= steps.indexOf(label) ? 'white' : 'rgba(255, 255, 255, 0.5)',
                  fontWeight: activeStep === steps.indexOf(label) ? 'bold' : 'normal',
                  fontSize: '0.9rem'
                }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {isAnalyzing ? (
          renderAnalyzing()
        ) : activeStep === questions.length ? (
          renderResults()
        ) : (
          renderQuestion()
        )}
      </MatchmakerContainer>
    </PageWrapper>
  );
};

export default DiamondMatchmaker; 